/*
  Quick add op collectiepagina's.

  Een tik op het rondje (+) op de productfoto opent een paneel met foto,
  prijs, kleuren en maten — zoals Zara, COS en Princess Polly dat doen. Een
  maat aantikken legt het product in de winkelwagen; het paneel sluit en
  onderin verschijnt een melding met "View cart". Heeft een product maar één
  variant, dan zit het er na de tik op het rondje meteen in.

  De productgegevens komen van /products/<handle>.js en worden pas bij de tik
  opgehaald (en onthouden), zodat de collectiepagina er niet zwaarder van
  wordt.

  De winkelwagen blijft dicht: de klant moet door kunnen shoppen en er een
  tweede en derde bij kunnen zetten. UpCart (de winkelwagen-app) klapt bij
  élke aanvraag naar /cart/add open, maar heeft zelf een uitzondering voor
  stille toevoegingen: ?upcart=1&opens_cart=never. Zonder UpCart negeert
  Shopify die parameters.
*/
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const tekst = Object.assign({
    added: 'Added',
    viewCart: 'View cart',
    soldOut: 'Sold out',
    close: 'Close',
    details: 'View full details',
    addToCart: 'Add to cart',
    error: "Couldn't add this item. Please try again.",
    moneyFormat: '${{amount}}'
  }, window.quickAddText || {});

  const MAAT = /size|maat|gr[öo](ss|ß)e|taille|talla|taglia|st[øo]rrelse|storlek|velikost|m[ăa]rime/i;
  const KLEUR = /colou?r|kleur|farbe|couleur|farve|f[äa]rg|barva|culoare|colore/i;
  const producten = new Map();

  /* ---------- hulpjes ---------- */
  function geld(centen) {
    const bedrag = (centen / 100);
    const metKomma = (n) => n.toFixed(2).replace('.', ',');
    return String(tekst.moneyFormat).replace(/\{\{\s*(\w+)\s*\}\}/, (_, soort) => {
      if (soort === 'amount_no_decimals') return Math.round(bedrag).toLocaleString('en');
      if (soort === 'amount_with_comma_separator') return metKomma(bedrag);
      if (soort === 'amount_no_decimals_with_comma_separator') return Math.round(bedrag).toLocaleString('de');
      return bedrag.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    });
  }
  function beeld(src, breedte) {
    if (!src) return '';
    const url = typeof src === 'string' ? src : src.src;
    if (!url) return '';
    return url + (url.includes('?') ? '&' : '?') + 'width=' + breedte;
  }
  function el(tag, klasse, eigenschappen) {
    const e = document.createElement(tag);
    if (klasse) e.className = klasse;
    if (eigenschappen) Object.assign(e, eigenschappen);
    return e;
  }
  function haalProduct(handle) {
    if (!producten.has(handle)) {
      producten.set(handle, fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => { if (!r.ok) throw new Error('product'); return r.json(); })
        .catch((e) => { producten.delete(handle); throw e; }));
    }
    return producten.get(handle);
  }

  /* ---------- melding onderin ---------- */
  let melding = null, meldingTimer = null;
  function toonMelding(regel, metKnop) {
    if (!melding) {
      melding = el('div', 'qa-toast');
      melding.setAttribute('role', 'status');
      melding.setAttribute('aria-live', 'polite');
      /* zolang de muis erop staat, blijft de melding staan */
      melding.addEventListener('mouseenter', () => clearTimeout(meldingTimer));
      melding.addEventListener('mouseleave', verbergLater);
      document.body.appendChild(melding);
    }
    melding.textContent = '';
    melding.appendChild(el('span', 'qa-toast__text', { textContent: regel }));
    if (metKnop) {
      const b = el('button', 'qa-toast__action', { type: 'button', textContent: tekst.viewCart });
      b.addEventListener('click', openWinkelwagen);
      melding.appendChild(b);
    }
    melding.classList.add('is-visible');
    verbergLater();
  }
  function verbergLater() {
    clearTimeout(meldingTimer);
    meldingTimer = setTimeout(() => melding.classList.remove('is-visible'), 5000);
  }

  function openWinkelwagen() {
    if (melding) melding.classList.remove('is-visible');
    const upcartAan = document.body.getAttribute('data-upcart-cart-enabled') === 'true';
    if (upcartAan && typeof window.upcartOpenCart === 'function') { window.upcartOpenCart(); return; }
    if (window.cartDrawer && typeof window.cartDrawer.open === 'function') {
      Promise.resolve(window.cartDrawer.refresh && window.cartDrawer.refresh()).finally(() => window.cartDrawer.open());
      return;
    }
    window.location.href = root + 'cart';
  }

  /* ---------- toevoegen ---------- */
  async function werkWinkelwagenBij() {
    try {
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      document.querySelectorAll('[data-cart-count]').forEach((n) => {
        n.textContent = cart.item_count;
        n.hidden = cart.item_count === 0;
      });
    } catch (e) { /* de teller is bijzaak; het product zit er al in */ }
    try { if (typeof window.upcartRefreshCart === 'function') await window.upcartRefreshCart(); } catch (e) {}
    try { if (window.cartDrawer && window.cartDrawer.refresh) await window.cartDrawer.refresh(); } catch (e) {}
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  async function voegToe(variantId, knop, label) {
    if (!variantId || (knop && knop.classList.contains('is-busy'))) return false;
    if (knop) { knop.classList.add('is-busy'); knop.setAttribute('aria-busy', 'true'); }
    try {
      const r = await fetch(root + 'cart/add.js?upcart=1&opens_cart=never', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        /* Shopify zegt zelf wat er mis is, bijvoorbeeld te weinig voorraad. */
        toonMelding(data.description || data.message || tekst.error, false);
        return false;
      }
      if (knop) knop.classList.add('is-added');
      toonMelding('✓ ' + tekst.added + ' · ' + label, true);
      werkWinkelwagenBij();
      return true;
    } catch (e) {
      toonMelding(tekst.error, false);
      return false;
    } finally {
      if (knop) { knop.classList.remove('is-busy'); knop.removeAttribute('aria-busy'); }
    }
  }

  /* ---------- het paneel ---------- */
  let paneel = null, lichaam = null, vorigeFocus = null, sluitTimer = null;

  function maakPaneel() {
    paneel = el('div', 'qa-sheet');
    paneel.hidden = true;
    const achter = el('div', 'qa-sheet__backdrop');
    achter.setAttribute('data-qa-close', '');
    const venster = el('div', 'qa-sheet__panel');
    venster.setAttribute('role', 'dialog');
    venster.setAttribute('aria-modal', 'true');
    venster.setAttribute('aria-labelledby', 'QaSheetTitle');
    venster.appendChild(el('div', 'qa-sheet__grip'));
    const dicht = el('button', 'qa-sheet__close', { type: 'button' });
    dicht.setAttribute('data-qa-close', '');
    dicht.setAttribute('aria-label', tekst.close);
    dicht.innerHTML = '<svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/></svg>';
    venster.appendChild(dicht);
    lichaam = el('div', 'qa-sheet__body');
    venster.appendChild(lichaam);
    paneel.append(achter, venster);
    document.body.appendChild(paneel);
    paneel.addEventListener('click', (e) => { if (e.target.closest('[data-qa-close]')) sluit(); });
  }

  function open() {
    clearTimeout(sluitTimer);
    paneel.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    void paneel.offsetWidth; /* eerst tekenen, dan pas de animatie starten */
    paneel.classList.add('is-open');
  }

  function sluit() {
    if (!paneel || paneel.hidden) return;
    paneel.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    sluitTimer = setTimeout(() => { paneel.hidden = true; lichaam.textContent = ''; }, 300);
    if (vorigeFocus && document.contains(vorigeFocus)) vorigeFocus.focus({ preventScroll: true });
  }

  function toonLaden(titel, voorlopig) {
    lichaam.textContent = '';
    const kop = el('div', 'qa-sheet__head');
    /* de foto uit de collectie staat al in de cache: meteen tonen */
    kop.appendChild(voorlopig ? el('img', 'qa-sheet__img', { src: voorlopig, alt: '' }) : el('div', 'qa-sheet__img'));
    const tekstblok = el('div');
    tekstblok.appendChild(el('p', 'qa-sheet__title', { id: 'QaSheetTitle', textContent: titel || '' }));
    tekstblok.appendChild(el('div', 'qa-sheet__skeleton', { style: 'width:40%' }));
    kop.appendChild(tekstblok);
    lichaam.appendChild(kop);
    lichaam.appendChild(el('div', 'qa-sheet__skeleton', { style: 'width:30%' }));
    lichaam.appendChild(el('div', 'qa-sheet__skeleton', { style: 'height:44px' }));
  }

  function toonProduct(p, voorlopig) {
    const opties = (p.options || []).map((o, i) => ({ naam: o.name || o, i, waarden: o.values || [] }));
    const maat = opties.find((o) => MAAT.test(o.naam)) || null;
    const overige = opties.filter((o) => o !== maat);
    const start = p.variants.find((v) => v.available) || p.variants[0];
    const keuze = start.options.slice();

    lichaam.textContent = '';

    /* kop: foto, titel (link naar het product), prijs */
    const kop = el('div', 'qa-sheet__head');
    const foto = el('img', 'qa-sheet__img', { alt: '', width: 76, height: 95, decoding: 'async' });
    if (voorlopig) foto.src = voorlopig;
    kop.appendChild(foto);
    const tekstblok = el('div');
    tekstblok.appendChild(el('a', 'qa-sheet__title', { id: 'QaSheetTitle', href: p.url || (root + 'products/' + p.handle), textContent: p.title }));
    const prijs = el('p', 'qa-sheet__price');
    tekstblok.appendChild(prijs);
    kop.appendChild(tekstblok);
    lichaam.appendChild(kop);

    const vind = (opts) => p.variants.find((v) => v.options.every((w, i) => w === opts[i]));
    /* voor de melding: eerst de maat, dan de kleur, want de naam ziet de klant al
       (en lange kleurnamen mogen de maat niet uit beeld duwen) */
    const gekozenTekst = (opts) => (maat ? [maat, ...overige] : overige)
      .filter((o) => o.waarden.length > 1).map((o) => opts[o.i]).join(' / ') || p.title;

    function werkKopBij() {
      /* De variant die nu gekozen is, of — zolang er nog geen maat is gekozen —
         de eerste in de gekozen kleur. Daarvan tonen we foto en prijs. */
      const zelfde = p.variants.filter((v) => overige.every((o) => v.options[o.i] === keuze[o.i]));
      const v = vind(keuze) || zelfde.find((x) => x.available) || zelfde[0] || start;
      const img = (v && v.featured_image) || p.featured_image || (p.images && p.images[0]);
      /* op de telefoon is de foto groot, op de computer een klein plaatje */
      const src = beeld(img, window.matchMedia('(max-width: 749px)').matches ? 720 : 200);
      /* pas wisselen als de nieuwe foto binnen is, zodat er nooit een leeg vlak staat */
      if (src && foto.dataset.doel !== src) {
        foto.dataset.doel = src;
        const nieuw = new Image();
        nieuw.onload = () => { if (foto.dataset.doel === src) foto.src = src; };
        nieuw.src = src;
      }
      prijs.textContent = '';
      if (v.compare_at_price && v.compare_at_price > v.price) prijs.appendChild(el('s', '', { textContent: geld(v.compare_at_price) }));
      prijs.appendChild(document.createTextNode(geld(v.price)));
    }

    /* keuzes voor kleur en eventuele andere opties */
    const optieRijen = [];
    overige.forEach((o) => {
      if (o.waarden.length < 2) return;
      const blok = el('div', 'qa-sheet__option');
      const label = el('p', 'qa-sheet__label');
      const gekozen = el('span');
      label.append(document.createTextNode(o.naam + ': '), gekozen);
      const rij = el('div', 'qa-sheet__swatches');
      rij.setAttribute('role', 'group');
      rij.setAttribute('aria-label', o.naam);
      const isKleur = KLEUR.test(o.naam);
      o.waarden.forEach((w) => {
        const b = el('button', 'qa-sheet__swatch', { type: 'button' });
        b.dataset.waarde = w;
        b.setAttribute('aria-label', w);
        const metFoto = isKleur && p.variants.find((v) => v.options[o.i] === w && v.featured_image);
        if (metFoto) {
          b.appendChild(el('img', '', { src: beeld(metFoto.featured_image, 120), alt: '', loading: 'lazy', width: 44, height: 55 }));
        } else {
          b.classList.add('qa-sheet__swatch--text');
          b.textContent = w;
        }
        b.addEventListener('click', () => { keuze[o.i] = w; teken(); });
        rij.appendChild(b);
      });
      blok.append(label, rij);
      lichaam.appendChild(blok);
      optieRijen.push({ o, rij, gekozen });
    });

    /* maten: een maat aantikken legt het product erin */
    let matenRij = null;
    if (maat) {
      const blok = el('div', 'qa-sheet__option');
      blok.appendChild(el('p', 'qa-sheet__label', { textContent: maat.naam }));
      matenRij = el('div', 'qa-sheet__sizes');
      matenRij.setAttribute('role', 'group');
      matenRij.setAttribute('aria-label', maat.naam);
      maat.waarden.forEach((w) => {
        const b = el('button', 'qa-sheet__size', { type: 'button', textContent: w });
        b.dataset.waarde = w;
        b.addEventListener('click', async () => {
          const opts = keuze.slice(); opts[maat.i] = w;
          const v = vind(opts);
          if (!v || !v.available) return;
          keuze[maat.i] = w;
          const gelukt = await voegToe(v.id, b, gekozenTekst(opts));
          if (gelukt) setTimeout(sluit, 350);
        });
        matenRij.appendChild(b);
      });
      blok.appendChild(matenRij);
      lichaam.appendChild(blok);
    }

    /* geen maat (bijvoorbeeld alleen een kleur): dan een gewone knop */
    let knop = null;
    if (!maat) {
      knop = el('button', 'qa-sheet__add', { type: 'button', textContent: tekst.addToCart });
      knop.addEventListener('click', async () => {
        const v = vind(keuze);
        if (!v || !v.available) return;
        const gelukt = await voegToe(v.id, knop, gekozenTekst(keuze));
        if (gelukt) setTimeout(sluit, 350);
      });
      lichaam.appendChild(knop);
    }

    /* onderaan: naar de productpagina. Zo staan de maten niet tegen de
       onderrand van de telefoon en kan wie meer wil weten verder kijken. */
    lichaam.appendChild(el('a', 'qa-sheet__details', { href: p.url || (root + 'products/' + p.handle), textContent: tekst.details }));

    function teken() {
      optieRijen.forEach(({ o, rij, gekozen }) => {
        gekozen.textContent = keuze[o.i];
        rij.querySelectorAll('.qa-sheet__swatch').forEach((b) => {
          b.setAttribute('aria-pressed', String(b.dataset.waarde === keuze[o.i]));
          const beschikbaar = p.variants.some((v) => v.available && v.options[o.i] === b.dataset.waarde);
          b.classList.toggle('is-soldout', !beschikbaar);
        });
      });
      if (matenRij) {
        matenRij.querySelectorAll('.qa-sheet__size').forEach((b) => {
          const opts = keuze.slice(); opts[maat.i] = b.dataset.waarde;
          const v = vind(opts);
          const ok = !!(v && v.available);
          b.disabled = !ok;
          b.classList.remove('is-added');
          if (ok) b.removeAttribute('aria-label');
          else b.setAttribute('aria-label', b.dataset.waarde + ' — ' + tekst.soldOut);
        });
      }
      if (knop) {
        const v = vind(keuze);
        knop.disabled = !(v && v.available);
        knop.textContent = knop.disabled ? tekst.soldOut : tekst.addToCart;
      }
      werkKopBij();
    }
    teken();

    const eerste = lichaam.querySelector('.qa-sheet__size:not([disabled]), .qa-sheet__swatch, .qa-sheet__add');
    if (eerste) eerste.focus({ preventScroll: true });
  }

  /* ---------- tikken en toetsen ---------- */
  document.addEventListener('click', async (event) => {
    const rondje = event.target.closest('[data-qa-open]');
    if (!rondje) return;
    event.preventDefault();

    const titel = rondje.dataset.qaTitle || '';
    if (rondje.dataset.qaVariant) {
      /* één variant: meteen erin, geen paneel nodig */
      const gelukt = await voegToe(rondje.dataset.qaVariant, rondje, titel);
      if (gelukt) setTimeout(() => rondje.classList.remove('is-added'), 1500);
      return;
    }

    if (!paneel) maakPaneel();
    vorigeFocus = rondje;
    const kaartFoto = rondje.closest('.product-tile')?.querySelector('img');
    const voorlopig = kaartFoto ? kaartFoto.currentSrc || kaartFoto.src : '';
    toonLaden(titel, voorlopig);
    open();
    try {
      toonProduct(await haalProduct(rondje.dataset.qaOpen), voorlopig);
    } catch (e) {
      /* Kan het product niet worden opgehaald, dan gewoon naar de productpagina. */
      window.location.href = root + 'products/' + rondje.dataset.qaOpen;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') sluit();
  });
})();
